import React from 'react'
import { Chip } from 'components/ui'
import { ArrowRight, Check } from 'lucide-react'
import { Result, Subtask } from 'constants/interfaces'
import keys from 'constants/keys'
import { useTranslateDefaultTitle } from 'components/TasksV2/hooks/useTranslateDefaultTitle'
import { useWorkspaceText } from '../../hooks/useWorkspaceText'
import { isExecutionComplete } from '../../model/execution'
import { ExecutionStatus } from '../ExecutionStatus'
import { MediaThumbnail } from '../ExecutionGallery'
import { SubtaskIcon } from './SubtaskIcon'
import { useSubtaskActionText } from './strings'
import styles from './styles.module.css'

export function useSubtaskTypeName() {
  const t = useWorkspaceText()
  return (subtask: Subtask) =>
    subtask.is_ai_enhanced
      ? t.ai
      : subtask.is_numeric
      ? t.number
      : ({ 2: t.photo, 3: t.link, 4: t.video, 5: t.file, 6: t.poll, 10: t.quiz, 8: t.text } as Record<number, string>)[
          subtask.type
        ] || t.text
}
export function ExecutionSubtaskCard({
  subtask,
  result,
  selected,
  disabled,
  readOnly = false,
  onSelect,
  actionLabel,
  layout = 'card',
}: {
  subtask: Subtask
  result?: Result
  selected: boolean
  disabled: boolean
  readOnly?: boolean
  onSelect: () => void
  actionLabel?: string
  layout?: 'card' | 'row'
}) {
  const t = useWorkspaceText()
  const typeName = useSubtaskTypeName()
  const title = useTranslateDefaultTitle(subtask.title)
  const completed = isExecutionComplete(result)
  const action = useSubtaskActionText(subtask, completed)
  const showCompleted = layout === 'row' && completed
  const visibleAction = showCompleted
    ? readOnly
      ? actionLabel || t.view
      : undefined
    : actionLabel || ((completed || (!selected && !readOnly)) && action)
  const media =
    result?.status === keys.TASK_STATUSES.SKIPPED.value
      ? []
      : subtask.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK
      ? result?.images || []
      : subtask.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS
      ? result?.videos || []
      : []
  const hasPhotoStack = subtask.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK && media.length > 1

  return (
    <button
      data-subtask-id={subtask.st_id}
      type="button"
      className={`${styles.card}${layout === 'row' ? ` ${styles.row}` : ''}`}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
    >
      <span className={styles.cardMain}>
        {media.length ? (
          <span className={`${styles.mediaPreview} ${hasPhotoStack ? styles.photoStack : ''}`}>
            {hasPhotoStack &&
              media.slice(1, 3).map((url, index) => (
                <span
                  key={`${url}-${index}`}
                  className={`${styles.thumbnailLayer} ${index === 0 ? styles.nearLayer : styles.farLayer}`}
                  aria-hidden="true"
                >
                  <MediaThumbnail url={url} mediaType="image" />
                </span>
              ))}
            <span className={styles.thumbnail}>
              <MediaThumbnail
                url={media[0]}
                mediaType={subtask.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS ? 'video' : 'image'}
              />
            </span>
          </span>
        ) : (
          <SubtaskIcon subtask={subtask} size={54} />
        )}
        <span className={styles.cardText}>
          <span className={styles.type}>{typeName(subtask)}</span>
          <strong>{title}</strong>
          <span className={styles.tags}>
            <ExecutionStatus status={result?.status} />
            {subtask.category && (
              <Chip
                as="span"
                label={<>{subtask.category}</>}
                size="sm"
                color="#ffffff"
                bg="#22272a"
                border="1px solid #22272a"
              />
            )}
          </span>
        </span>
      </span>
      {(showCompleted || visibleAction) && (
        <span className={styles.actions}>
          {showCompleted && (
            <span className={styles.completed} role="img" aria-label={t.done} title={t.done}>
              <Check size={14} strokeWidth={2.5} aria-hidden="true" />
            </span>
          )}
          {visibleAction && (
            <span className={styles.action}>
              {visibleAction}
              <ArrowRight size={15} aria-hidden="true" />
            </span>
          )}
        </span>
      )}
    </button>
  )
}
